/**
 * Asset record schema — single source of truth for both the DataTable
 * columns and (once Story 4.D lands) the FormModal field definitions.
 *
 * Real DB shape (from analysis_agent → asset_identification_agent):
 *   { asset_id, asset_name, asset_description, asset_type,
 *     security_properties: string[] }
 */
export interface AssetRecord {
  asset_id: string;
  asset_name?: string;
  asset_description?: string;
  asset_type?: string;
  security_properties?: string[];
  [key: string]: unknown;
}

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

/** DataTable column descriptors. Widths are percentage strings (the
 *  app's DataTable convention — see ProjectsTable / ItemTable).
 *
 *  Two sets, each summing to 100%:
 *    - ASSET_COLUMNS_READ — read-only mode (5 columns)
 *    - ASSET_COLUMNS_EDIT — edit mode (5 columns + 4% _actions kebab)
 *
 *  Note: DataTable invokes `column.render(row)` — passing the full
 *  row, not the cell value. Pull the key off `row` inside `render`. */
export const ASSET_COLUMNS_READ = [
  { key: 'asset_id', label: 'ID', sortable: true, width: '11%' },
  { key: 'asset_name', label: 'Name', sortable: true, width: '23%' },
  { key: 'asset_type', label: 'Type', sortable: true, width: '13%' },
  { key: 'asset_description', label: 'Description', sortable: false, width: '34%' },
  {
    key: 'security_properties',
    label: 'Security Properties',
    sortable: false,
    width: '19%',
    render: (row: AssetRecord) => joinList(row.security_properties),
  },
];

export const ASSET_COLUMNS_EDIT = [
  { key: 'asset_id', label: 'ID', sortable: true, width: '10%' },
  { key: 'asset_name', label: 'Name', sortable: true, width: '22%' },
  { key: 'asset_type', label: 'Type', sortable: true, width: '12%' },
  { key: 'asset_description', label: 'Description', sortable: false, width: '32%' },
  {
    key: 'security_properties',
    label: 'Security Properties',
    sortable: false,
    width: '20%',
    render: (row: AssetRecord) => joinList(row.security_properties),
  },
  // The trailing _actions column is appended by AssetTable in edit
  // mode; its width is the remaining 4% to keep the total at 100%.
];

export const ASSET_ACTIONS_COLUMN_WIDTH = '4%';

/** Back-compat alias — earlier story exported a single ASSET_COLUMNS.
 *  Keep the symbol so any in-flight imports keep compiling; resolves
 *  to the read-mode column set. */
export const ASSET_COLUMNS = ASSET_COLUMNS_READ;

/** FormModal field config — used by AssetFormModal for Add / Edit.
 *  Mirrors the legacy `assetFieldSchema` (fieldSchemas.js) translated
 *  into the shared FormModal contract (`name` instead of `key`,
 *  `validate` callback instead of `validation` object, etc.). */
const ASSET_ID_PATTERN = /^[A-Z0-9_-]+$/;

export const ASSET_FORM_FIELDS = [
  {
    name: 'asset_id',
    label: 'Asset ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. ASSET-001, CAMERA-FRONT',
    helpText: 'Unique identifier — uppercase letters, numbers, underscores, hyphens.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Asset ID is required';
      if (!ASSET_ID_PATTERN.test(s)) {
        return 'Asset ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'asset_type',
    label: 'Asset Type',
    type: 'select',
    required: true,
    options: [
      { value: 'Hardware', label: 'Hardware' },
      { value: 'Software', label: 'Software' },
      { value: 'Data', label: 'Data' },
      { value: 'Communication', label: 'Communication' },
      { value: 'Function', label: 'Function' },
    ],
    helpText: 'Asset category per ISO/SAE 21434.',
    width: 'half',
  },
  {
    name: 'asset_name',
    label: 'Asset Name',
    type: 'text',
    required: true,
    placeholder: 'Descriptive name for this asset',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 3) return 'Name must be at least 3 characters';
      if (s.length > 100) return 'Name must be at most 100 characters';
      return null;
    },
  },
  {
    name: 'security_properties',
    label: 'Security Properties',
    type: 'checkbox-group',
    required: true,
    options: [
      { value: 'Confidentiality', label: 'Confidentiality' },
      { value: 'Integrity', label: 'Integrity' },
      { value: 'Availability', label: 'Availability' },
      { value: 'Authenticity', label: 'Authenticity' },
      { value: 'Non-repudiation', label: 'Non-repudiation' },
      { value: 'Authorization', label: 'Authorization' },
    ],
    helpText: 'Security properties that need protection for this asset.',
    defaultValue: [],
  },
  {
    name: 'asset_description',
    label: 'Description',
    type: 'textarea',
    required: true,
    rows: 4,
    placeholder: 'What the asset is and why it needs protection',
    helpText: 'Detailed description, including the asset’s security relevance.',
  },
];
